import { z } from "zod";

/**
 * Service-requests shared contract (Strangler-Fig migration, manifest-6).
 *
 * Single source of truth for the `service_requests` wire shape shared by the
 * web SPA (`src/repositories/serviceRequests.ts`) and the NestJS slice
 * (`apps/api/src/service-requests/*`). The shape is **snake_case** so the
 * frontend<->Nest transport is a field-for-field swap (no renames) — matching the
 * Nest `Create/UpdateServiceRequestDto` and the FE `ServiceRequest` type.
 *
 * The web repo `.parse()`s Nest responses against {@link serviceRequestSchema}
 * at the flip boundary, so any backend/frontend drift surfaces at runtime
 * (and the repo can fall back to the Supabase path).
 *
 * BE adoption note: `apps/api` does not yet import this package — it keeps its
 * class-validator DTO (already shape-aligned). Wiring the Nest side to consume
 * `@dallali/shared` needs a dual ESM/CJS build step and is tracked as deferred
 * backlog in `docs/migration/domain-debugging-matrix.md`.
 */

/** Mirrors the Supabase generated `Json` type so inferred fields stay `Json | null`. */
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[];

export const jsonSchema: z.ZodType<Json> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonSchema),
    z.record(jsonSchema),
  ]),
);

/** Lifecycle statuses (advisory — the row schema keeps `status` an open string to never reject server data). */
export const SERVICE_REQUEST_STATUSES = [
  "new",
  "under_inspection",
  "under_review",
  "approved",
  "rejected",
  "completed",
] as const;
export const serviceRequestStatusSchema = z.enum(SERVICE_REQUEST_STATUSES);
export type ServiceRequestStatus = (typeof SERVICE_REQUEST_STATUSES)[number];

/**
 * A persisted service request as the frontend consumes it. Field set + nullability
 * are byte-identical to the previous hand-written `ServiceRequest` interface so it
 * can be re-exported as the single source of truth without changing any consumer.
 */
export const serviceRequestSchema = z.object({
  id: z.string(),
  request_number: z.string().nullable(),
  company_id: z.string().nullable(),
  phone: z.string(),
  national_id: z.string(),
  property_registry_number: z.string().nullable(),
  // Resolved from the joined property (owner-requests endpoint) — the owner's
  // identifier for the request. Optional so callers/fixtures without the join
  // need not supply it (the owner list falls back to the service label).
  property_name: z.string().nullable().optional(),
  property_type: z.string().nullable(),
  property_city: z.string().nullable(),
  property_district: z.string().nullable(),
  property_area: z.number().nullable(),
  service_type: z.string(),
  commission_rate: z.number(),
  status: z.string(),
  nafath_verified: z.boolean().nullable(),
  terms_accepted: z.boolean().nullable(),
  notes: z.string().nullable(),
  inspection_date: z.string().nullable(),
  inspection_report: jsonSchema.nullable(),
  rejection_reason: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  scheduling_token: z.string().nullable(),
  scheduled_date: z.string().nullable(),
  scheduled_time: z.string().nullable(),
  property_number: z.string().nullable(),
  unit_number: z.string().nullable(),
  access_method: z.string().nullable(),
  access_code: z.string().nullable(),
  representative_name: z.string().nullable(),
  representative_phone: z.string().nullable(),
  assigned_employee_id: z.string().nullable(),
  deed_document_url: z.string().nullable(),
  is_manual_entry: z.boolean().nullable(),
  /**
   * Populated client-side from `nafath_verifications`; **not a source column** —
   * so it's `.optional()`: the Nest/Supabase row won't include it, and the repo
   * adds it via `enrichWithRequesterNames()` AFTER parsing. Requiring it made the
   * Nest list response fail Zod and silently fall back to Supabase (the flip was
   * a no-op). @backend-todo when Nest becomes the sole source, have the endpoint
   * replicate the `nafath_verifications` join.
   */
  requester_name: z.string().nullable().optional(),
  contract_file_url: z.string().nullable(),
  property_status: z.string().nullable(),
  // Optional so the parser tolerates a source that omits them (e.g. a Nest
  // serializer that doesn't yet project these columns); the Supabase path still
  // supplies them. @backend-todo ensure the Nest SR endpoint returns min/max_price.
  min_price: z.number().nullable().optional(),
  max_price: z.number().nullable().optional(),
  properties_json: jsonSchema.nullable(),
  reviewed_by: z.string().nullable(),
  // Master Card lifecycle status + completion %, projected by the owner-requests
  // (and admin) list endpoints so the owner list shows the SAME status the admin
  // sees (B1) instead of the legacy inspection `status`. `card_status` is NOT
  // NULL in the DB (default 'new'), so it's a plain string — `.optional()` (never
  // `.nullable()`) so it stays assignable to the non-null Supabase column type.
  // Optional overall: the Supabase fallback's select("*") supplies `card_status`
  // but not the computed `completion_pct`, and other callers/fixtures may omit both.
  card_status: z.string().optional(),
  completion_pct: z.number().optional(),
});

export type ServiceRequest = z.infer<typeof serviceRequestSchema>;

/**
 * Create payload — mirrors the Nest `CreateServiceRequestDto`. The three identity
 * fields are required (every create caller populates them); the rest are optional.
 * (Create stays on the Supabase path for now — owner-portal callers lack
 * `service_requests.create`; included here for contract completeness.)
 */
export const createServiceRequestSchema = z.object({
  phone: z.string().min(1),
  national_id: z.string().min(1),
  service_type: z.string().min(1),
  request_number: z.string().optional(),
  property_registry_number: z.string().optional(),
  property_type: z.string().optional(),
  property_city: z.string().optional(),
  property_district: z.string().optional(),
  property_area: z.number().optional(),
  commission_rate: z.number().optional(),
  status: z.string().optional(),
  nafath_verified: z.boolean().optional(),
  terms_accepted: z.boolean().optional(),
  notes: z.string().optional(),
  inspection_date: z.string().optional(),
  inspection_report: jsonSchema.optional(),
  rejection_reason: z.string().optional(),
  scheduling_token: z.string().optional(),
  scheduled_date: z.string().optional(),
  scheduled_time: z.string().optional(),
  property_number: z.string().optional(),
  unit_number: z.string().optional(),
  access_method: z.string().optional(),
  access_code: z.string().optional(),
  representative_name: z.string().optional(),
  representative_phone: z.string().optional(),
  assigned_employee_id: z.string().optional(),
  deed_document_url: z.string().optional(),
  is_manual_entry: z.boolean().optional(),
  requester_name: z.string().optional(),
  contract_file_url: z.string().optional(),
  property_status: z.string().optional(),
  min_price: z.number().optional(),
  max_price: z.number().optional(),
  properties_json: jsonSchema.optional(),
  reviewed_by: z.string().optional(),
});

export type CreateServiceRequest = z.infer<typeof createServiceRequestSchema>;

/** Update payload — every field optional (partial replacement); mirrors the Nest `UpdateServiceRequestDto`. */
export const updateServiceRequestSchema = createServiceRequestSchema.partial();

export type UpdateServiceRequest = z.infer<typeof updateServiceRequestSchema>;
